import React, { useEffect, useMemo, useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import { TailSpin } from "react-loader-spinner";

import type { CreateInviteCodeBody } from "../../lib/api/inviteCodes";
import type {
  CreateInviteCodeInput,
  InviteCodeEligibilityUi,
} from "./inviteCodesTypes";
import RequiredPairsSelect from "./RequiredPairsSelect";

type Props = {
  open: boolean;
  onClose: () => void;
  submitting?: boolean;
  onCreate: (body: CreateInviteCodeBody) => void;
};

const emptyForm: CreateInviteCodeInput = {
  code: "",
  ownerEmail: "",
  depositReward: 100,
  minDepositUSD: 20,
  tradeReward: 100,
  minTradeUSD: 50,
  requiredPairs: [],
  eligibility: "ALL",
  expiryDate: "",
  maxMembers: "",
};

const ELIGIBLE: { id: InviteCodeEligibilityUi; label: string }[] = [
  { id: "ALL", label: "All Users" },
  { id: "NEW_USERS", label: "New Users" },
  { id: "EXISTING_USERS", label: "Existing Users" },
];

const CODE_RE = /^[A-Za-z0-9]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function numOrEmpty(v: number | ""): number | undefined {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function buildCreateInviteCodeBody(
  form: CreateInviteCodeInput
): { ok: true; body: CreateInviteCodeBody } | { ok: false; error: string } {
  const codeRaw = form.code.trim().toUpperCase();
  if (codeRaw) {
    if (codeRaw.length < 3 || codeRaw.length > 24) {
      return { ok: false, error: "Code must be 3–24 characters." };
    }
    if (!CODE_RE.test(codeRaw)) {
      return { ok: false, error: "Code must be alphanumeric only." };
    }
  }

  const ownerEmail = form.ownerEmail.trim();
  if (ownerEmail && !EMAIL_RE.test(ownerEmail)) {
    return { ok: false, error: "Enter a valid owner email." };
  }

  const depositReward = numOrEmpty(form.depositReward);
  const minDepositUSD = numOrEmpty(form.minDepositUSD);
  const tradeReward = numOrEmpty(form.tradeReward);
  const minTradeUSD = numOrEmpty(form.minTradeUSD);

  if (depositReward != null && minDepositUSD == null) {
    return {
      ok: false,
      error: "Min deposit USD is required when deposit reward is set.",
    };
  }
  if (tradeReward != null && minTradeUSD == null) {
    return {
      ok: false,
      error: "Min trade USD is required when trade reward is set.",
    };
  }

  const maxMembers = numOrEmpty(form.maxMembers);
  const expiryDate = form.expiryDate.trim();

  if (!expiryDate && (maxMembers == null || maxMembers < 1)) {
    return {
      ok: false,
      error: "Set an expiry date or a max members count (≥ 1).",
    };
  }
  if (maxMembers != null && maxMembers < 1) {
    return { ok: false, error: "Max members must be at least 1." };
  }
  if (expiryDate) {
    const expires = new Date(`${expiryDate}T00:00:00.000Z`);
    if (Number.isNaN(expires.getTime()) || expires.getTime() <= Date.now()) {
      return { ok: false, error: "Expiry must be a future date." };
    }
  }

  const body: CreateInviteCodeBody = {
    eligibility: form.eligibility,
  };
  if (codeRaw) body.code = codeRaw;
  if (ownerEmail) body.ownerEmail = ownerEmail;
  if (depositReward != null) body.depositReward = depositReward;
  if (minDepositUSD != null) body.minDepositUSD = minDepositUSD;
  if (tradeReward != null) body.tradeReward = tradeReward;
  if (minTradeUSD != null) body.minTradeUSD = minTradeUSD;
  if (form.requiredPairs.length) {
    body.requiredPairs = form.requiredPairs.map((p) => p.toUpperCase());
  }
  if (expiryDate) body.expiresAt = `${expiryDate}T00:00:00.000Z`;
  if (maxMembers != null) body.maxMembers = Math.floor(maxMembers);

  return { ok: true, body };
}

const CreateInviteCodeModal: React.FC<Props> = ({
  open,
  onClose,
  submitting,
  onCreate,
}) => {
  const [form, setForm] = useState<CreateInviteCodeInput>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, submitting]);

  const totalPts = useMemo(() => {
    const d = numOrEmpty(form.depositReward) ?? 0;
    const t = numOrEmpty(form.tradeReward) ?? 0;
    return d + t;
  }, [form.depositReward, form.tradeReward]);

  if (!open) return null;

  const setField = <K extends keyof CreateInviteCodeInput>(
    key: K,
    value: CreateInviteCodeInput[K]
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const built = buildCreateInviteCodeBody(form);
    if (!built.ok) {
      setError(built.error);
      return;
    }
    setError(null);
    onCreate(built.body);
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-black/75 p-4 font-manrope backdrop-blur-[3px]"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="relative my-auto flex max-h-[min(92vh,720px)] w-full max-w-[520px] flex-col overflow-hidden rounded-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-invite-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[#2C363E] px-5 py-4 sm:px-6">
          <div>
            <h2
              id="create-invite-title"
              className="font-sora text-lg font-bold text-white"
            >
              Create Invite Code
            </h2>
            <p className="mt-1 text-[12px] text-[#869AA9]">
              Configure rewards and eligibility for new codes.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-xl p-2 text-[#869AA9] transition hover:bg-[#273037] hover:text-white disabled:opacity-50"
            aria-label="Close"
          >
            <FaTimesCircle className="text-2xl" />
          </button>
        </header>

        <form
          onSubmit={handleSubmit}
          className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label
                htmlFor="invite-code"
                className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
              >
                Code (optional, auto-generated if empty)
              </label>
              <input
                id="invite-code"
                value={form.code}
                onChange={(e) =>
                  setField(
                    "code",
                    e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase()
                  )
                }
                maxLength={24}
                placeholder="e.g. DXSUMMER100"
                className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
              />
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="invite-owner-email"
                className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
              >
                Assign to Inviter email (optional)
              </label>
              <input
                id="invite-owner-email"
                type="email"
                value={form.ownerEmail}
                onChange={(e) => setField("ownerEmail", e.target.value)}
                placeholder="e.g. paulambrose2005@gmail.com"
                className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
              />
            </div>

            <div>
              <label
                htmlFor="invite-dep-pts"
                className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
              >
                Deposit Reward (pts)
              </label>
              <input
                id="invite-dep-pts"
                type="number"
                min={0}
                value={form.depositReward}
                onChange={(e) =>
                  setField(
                    "depositReward",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
              />
            </div>

            <div>
              <label
                htmlFor="invite-trade-pts"
                className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
              >
                Trade Reward (pts)
              </label>
              <input
                id="invite-trade-pts"
                type="number"
                min={0}
                value={form.tradeReward}
                onChange={(e) =>
                  setField(
                    "tradeReward",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
              />
            </div>

            <div>
              <label
                htmlFor="invite-min-dep"
                className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
              >
                Min Deposit ($)
              </label>
              <input
                id="invite-min-dep"
                type="number"
                min={0}
                value={form.minDepositUSD}
                onChange={(e) =>
                  setField(
                    "minDepositUSD",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
              />
            </div>

            <div>
              <label
                htmlFor="invite-min-trade"
                className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
              >
                Min Trade ($)
              </label>
              <input
                id="invite-min-trade"
                type="number"
                min={0}
                value={form.minTradeUSD}
                onChange={(e) =>
                  setField(
                    "minTradeUSD",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
              />
            </div>

            <div className="sm:col-span-2">
              <p className="mb-1.5 text-[11px] font-medium text-[#869AA9]">
                Required assets (empty = any)
              </p>
              <RequiredPairsSelect
                value={form.requiredPairs}
                onChange={(next) => setField("requiredPairs", next)}
                disabled={submitting}
              />
            </div>

            <div>
              <label
                htmlFor="invite-expiry"
                className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
              >
                Expiry Date
              </label>
              <input
                id="invite-expiry"
                type="date"
                value={form.expiryDate}
                onChange={(e) => setField("expiryDate", e.target.value)}
                className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
              />
            </div>

            <div>
              <label
                htmlFor="invite-max-members"
                className="mb-1.5 block text-[11px] font-medium text-[#869AA9]"
              >
                Max Members (optional)
              </label>
              <input
                id="invite-max-members"
                type="number"
                min={1}
                value={form.maxMembers}
                onChange={(e) =>
                  setField(
                    "maxMembers",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="h-10 w-full rounded-lg border border-[#2C363E] bg-[#161E25] px-3 text-[13px] text-[#E7EBEE] outline-none focus:border-secondary_button focus:ring-1 focus:ring-secondary_button/40"
              />
            </div>

            <p className="sm:col-span-2 text-[11px] text-[#5C6B76]">
              Provide expiry and/or max members (at least one is required).
            </p>

            <div className="sm:col-span-2">
              <p className="mb-2 text-[11px] font-medium text-[#869AA9]">
                Eligible Users
              </p>
              <div className="flex flex-wrap gap-2" role="group">
                {ELIGIBLE.map((opt) => {
                  const selected = form.eligibility === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setField("eligibility", opt.id)}
                      className={`rounded-lg border px-3 py-2 text-[12px] font-semibold transition ${
                        selected
                          ? "border-secondary_button bg-secondary_button/15 text-secondary_button"
                          : "border-[#2C363E] text-[#869AA9] hover:border-[#3A4852] hover:text-[#C6D0D7]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-[#2C363E] bg-[#161E25]/70 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#869AA9]">
              Total Reward Preview
            </p>
            <p className="mt-1 font-sora text-lg font-bold text-white">
              {totalPts.toLocaleString()} DeeXpoints
            </p>
          </div>

          {error && (
            <p className="mt-3 text-[12px] font-medium text-amber-400/95">
              {error}
            </p>
          )}

          <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="h-11 rounded-xl border border-[#3A4852] bg-[#1C2227] px-5 text-[13px] font-semibold text-[#BEE1FB] transition hover:bg-[#273037] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex h-11 items-center justify-center gap-2 rounded-xl bg-secondary_button px-5 text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(11,132,203,0.35)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <TailSpin color="#fff" height={20} width={20} />
              ) : null}
              Create Code
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInviteCodeModal;
